import CRMPageTemplate from '@components/Common/CRMPageTemplate';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import ErrorMessage from '@components/Common/ErrorMessage';
import MatchPercent from '@components/Common/MatchPercent';
import type { Column } from '@components/Common/SearchFilter/SearchFilter';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import type { ColumnsConfig } from '@components/Common/Table/cardFields';
import { useToast } from '@components/Common/Toast/ToastProvider';
import { CLOSED_REASONS, STAGES, STATUS_VALUES, resolveStatus, statusToStage } from '@constants/applicationStatus';
import { companyNameSearch, jobApplicantsList, jobList } from '@constants/graphQLQueries';
import { candidateDisplayName } from '@helpers/candidateRecord';
import { exportApplicationsExcel } from '@helpers/exportApplicationsExcel';
import { exportSubmissionsWorkbook } from '@helpers/exportSubmissionsWorkbook';
import { isSuperAdmin, useCurrentUserRole } from '@components/Common/Utils/roles';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import { graphQLFilterFormatter } from '@helpers/utils';
import AssignmentIcon from '@mui/icons-material/Assignment';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import AutocompleteFormField from '@components/Common/AutocompleteFormField';
import { Box, Button, Chip, CircularProgress, Typography } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDateTime, formatDayMonth } from '@helpers/date';

/* The table's columns, named once so the loading skeleton can draw the same number of
   them. A skeleton with its own column count is a skeleton that stops matching. */
const APPLICANT_COLUMNS = ['name', 'match', 'designation', 'companyName', 'status', 'detail', 'hr', 'nextAction', 'createdAt'];

/* The stage is fourth, so the tablet default (the first three) dropped it. Keep it on tablet. */
const APPLICANT_COLUMN_LAYOUT: ColumnsConfig = {
  name: { card: 'title', tablet: true },
  designation: { card: 'subtitle', tablet: true },
  status: { card: 'status', tablet: true },
};

/** Every detail, in stage order, for the Detail filter. */
const DETAIL_VALUES = Array.from(new Set(STAGES.flatMap((s) => s.details)));

interface JobApplicant {
  id: string;
  name: string;
  designation: string;
  companyName: string;
  status: string;
  stage: string;
  /** The detail, or for a Closed record its reason. */
  detail: string;
  hrIds: string[];
  ownerId?: string;
  nextAction?: string;
  nextActionAt?: string;
  createdAt?: string;
  /** 0 to 100, null when the candidate or the job has no profile vector yet. */
  match?: number | null;
  matchStatus?: string | null;
}

/** Detail for an open record, reason for a closed one: whichever says more about where they are. */
const detailOf = (item: any): string => {
  const r = resolveStatus(item.status, item.subStatus, item.closedReason);
  if (r.stage?.label === 'Closed') return r.closedReason;
  return r.detail && r.detail !== r.stage?.label ? r.detail : '';
};

interface JobOption {
  value: string; // job _id
  label: string; // "Title - Company"
}

const rowsPerPage = 100;

const JobApplicants: React.FC = () => {
  const { showToast } = useToast();

  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(() => {
    const p = parseInt(searchParams.get('page') ?? '0', 10);
    return Number.isFinite(p) && p >= 0 ? p : 0;
  });
  const [data, setData] = useState<JobApplicant[]>([]);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [exportingWorkbook, setExportingWorkbook] = useState<boolean>(false);
  const superAdmin = isSuperAdmin(useCurrentUserRole());

  const handleExportWorkbook = async () => {
    setExportingWorkbook(true);
    try {
      const truncated = await exportSubmissionsWorkbook();
      // A short workbook is the one people act on without noticing. The file carries a Coverage
      // sheet, but the toast is what reaches someone who downloaded it and moved on.
      if (truncated.length) {
        showToast(
          'Workbook downloaded, but it is incomplete. See the Coverage sheet.',
          'warning',
        );
      } else {
        showToast('Submissions workbook downloaded', 'success');
      }
    } catch (err) {
      console.error('Failed to export submissions workbook', err);
      showToast('Could not generate the workbook. Please try again.', 'error');
    } finally {
      setExportingWorkbook(false);
    }
  };
  const [error, setError] = useState<string | null>(null);
  const [filterData, setFilterData] = useState<Column[]>(() => {
    const initial: Column[] = [];
    searchParams.forEach((value, key) => {
      if (key.startsWith('filter_') && value.trim() !== '') {
        const col = key.replace('filter_', '');
        initial.push({ id: initial.length + 1, columnName: col, searchValue: decodeURIComponent(value) });
      }
    });
    return initial;
  });
  const [dateFrom, setDateFrom] = useState(() => searchParams.get('date_from') ?? '');
  const [dateTo, setDateTo] = useState(() => searchParams.get('date_to') ?? '');
  const [hrById, setHrById] = useState<Record<string, string>>({});
  const [jobOptions, setJobOptions] = useState<JobOption[]>([]);
  // Company Name + Job are multi-select. Panel is staged-then-Apply: `pending*` is what the
  // control shows, `selected*` is what the query uses (committed on Apply).
  const [companyOpts, setCompanyOpts] = useState<string[]>([]); // async search results
  const [pendingCompanies, setPendingCompanies] = useState<string[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  // Deep-link support: ?jobId=<id>&jobLabel=<text> (e.g. from My Accounts "View candidates")
  // pre-applies the Job filter. Seed at init so the very first fetch is already scoped.
  const initialJobs: JobOption[] = (() => {
    const jobId = searchParams.get('jobId');
    return jobId ? [{ value: jobId, label: searchParams.get('jobLabel') || jobId }] : [];
  })();
  const [pendingJobs, setPendingJobs] = useState<JobOption[]>(initialJobs);
  const [selectedJobs, setSelectedJobs] = useState<JobOption[]>(initialJobs);
  const companySearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const skipResetOnFirstFilterChange = useRef(true);
  const navigate = useNavigate();

  // Debounced server-side company search feeding the Company Name multi-select options.
  const onCompanyInput = useCallback((query: string) => {
    if (companySearchTimer.current) clearTimeout(companySearchTimer.current);
    if (!query.trim()) {
      setCompanyOpts([]);
      return;
    }
    companySearchTimer.current = setTimeout(async () => {
      try {
        const { data } = await fetchGraphQLApiData(companyNameSearch(query.trim(), 20));
        setCompanyOpts(
          Array.from(
            new Set(
              ((data?.getAllCompanies?.content ?? []) as any[])
                .map((c) => c.companyName as string)
                .filter((n): n is string => Boolean(n)),
            ),
          ),
        );
      } catch {
        setCompanyOpts([]);
      }
    }, 350);
  }, []);

  useEffect(() => {
    // HR id -> name map so the assigned-HR column can show names (the sheet's "HR" column).
    axios.get('/api/users?page=0&size=200').then((res) => {
      const map: Record<string, string> = {};
      (res.data?.content ?? []).forEach((u: any) => {
        map[u._id] =
          u.personalDetails?.fullName ||
          `${u.personalDetails?.firstName ?? ''} ${u.personalDetails?.lastName ?? ''}`.trim() ||
          u.personalDetails?.email ||
          'HR';
      });
      setHrById(map);
    }).catch(() => {});
    // Jobs list for the "filter by specific job" dropdown.
    fetchGraphQLApiData(jobList(0, 200, [])).then(({ data }) => {
      const opts: JobOption[] = (data?.getAllJobs?.content ?? [])
        .filter((j: any) => j?._id)
        .map((j: any) => ({
          value: j._id,
          label: [j.title, j.companyName].filter(Boolean).join(' - ') || j._id,
        }));
      setJobOptions(opts);
    }).catch(() => {});
  }, []);

  const hrNamesFor = (ids: string[]) => ids.map((id) => hrById[id] || 'HR');

  // Company Name and Job are rendered as multi-select controls at the top of the panel
  // (page-managed, not SearchFilter columns), so they're not in this mapping.
  const jobApplicantsColMapping = {
    Name: 'jobSeeker.personalDetails.fullName',
    Designation: 'job.title',
    Stage: 'status',
    Detail: 'subStatus',
    'Closed reason': 'closedReason',
  };
  const filterColumns = Object.keys(jobApplicantsColMapping);

  const onApplyFilters = useCallback((_filters: Column[], clearAll?: boolean) => {
    // Commit the staged Company + Job selections. Done before the skip guard below, otherwise
    // the panel's first Apply would drop them. "Clear All" wipes them like any other filter.
    if (clearAll) {
      setPendingCompanies([]);
      setPendingJobs([]);
      setSelectedCompanies([]);
      setSelectedJobs([]);
    } else {
      setSelectedCompanies(pendingCompanies);
      setSelectedJobs(pendingJobs);
    }

    if (skipResetOnFirstFilterChange.current) {
      skipResetOnFirstFilterChange.current = false;
      return;
    }
    if (page !== 0) setPage(0);
  }, [page, pendingCompanies, pendingJobs]);

  const handleChangePage = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      setPage(newPage);
      const newSearch = new URLSearchParams(searchParams);
      newSearch.set('page', String(newPage));
      if (newSearch.toString() !== searchParams.toString()) setSearchParams(newSearch);
    },
    [searchParams, setSearchParams],
  );

  const fetchData = useCallback(
    async (page: number) => {
      setLoading(true);
      setError(null);
      const filter = graphQLFilterFormatter(filterData, jobApplicantsColMapping);
      if (dateFrom) filter.push(`{ key: "createdAtFrom", value: "${dateFrom}" }`);
      if (dateTo) filter.push(`{ key: "createdAtTo", value: "${dateTo}" }`);
      // Multi-value company + job: the backend groups same-key values with OR, different keys with AND.
      selectedCompanies.forEach((c) =>
        filter.push(`{ key: "job.companyName", value: ${JSON.stringify(c)} }`),
      );
      selectedJobs.forEach((j) => filter.push(`{ key: "jobId", value: "${j.value}" }`));
      try {
        const query = jobApplicantsList(page, rowsPerPage, filter);
        const { data } = await fetchGraphQLApiData(query);
        const { getAllJobApplications } = data;
        // Check the SHAPE, not truthiness: totalElements of 0 is falsy, so a legitimately empty
        // result (a filter that matches nothing, or an HR whose scope returns an empty page) used
        // to fall through to the error branch and surface as "Failed to fetch job applications".
        if (getAllJobApplications && Array.isArray(getAllJobApplications.content)) {
          const jobApplicants: JobApplicant[] = getAllJobApplications.content.map(
            (item: any) => ({
              id: item._id,
              name: candidateDisplayName(item),
              designation: item.job?.title || '',
              companyName: item.job?.companyName || '',
              status: item.status || '',
              stage: statusToStage(item.status) || '',
              detail: detailOf(item),
              hrIds: item.assignedHRs ?? [],
              ownerId: item.ownerId || '',
              nextAction: item.nextAction || '',
              nextActionAt: item.nextActionAt || '',
              createdAt: item.createdAt || '',
              match: item.relevancyPercent ?? null,
              matchStatus: item.relevancyStatus ?? null,
            }),
          );
          setData(jobApplicants);
          setTotalElements(getAllJobApplications.totalElements ?? 0);
        } else {
          showToast('Unexpected response format', 'error');
          throw new Error('Unexpected response format');
        }
      } catch (error) {
        console.error('Error fetching job applications:', error);
        setError('Failed to fetch data. Please try again later.');
        showToast('Failed to fetch job applications', 'error');
      } finally {
        setLoading(false);
      }
    },
    [filterData, dateFrom, dateTo, selectedCompanies, selectedJobs, showToast],
  );

  useEffect(() => { fetchData(page); }, [fetchData, page, filterData]);

  useEffect(() => {
    const p = parseInt(searchParams.get('page') ?? '0', 10);
    const safe = Number.isFinite(p) && p >= 0 ? p : 0;
    if (safe !== page) setPage(safe);
    setDateFrom(searchParams.get('date_from') ?? '');
    setDateTo(searchParams.get('date_to') ?? '');
  }, [searchParams.toString()]);

  const skipDateUrlSyncOnMount = useRef(true);
  useEffect(() => {
    if (skipDateUrlSyncOnMount.current) {
      skipDateUrlSyncOnMount.current = false;
      return;
    }
    setSearchParams(prev => {
      const n = new URLSearchParams(prev);
      if (dateFrom) n.set('date_from', dateFrom); else n.delete('date_from');
      if (dateTo) n.set('date_to', dateTo); else n.delete('date_to');
      return n;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  /** The filters currently in force, as sent to the server. Shared by the table and the export. */
  const buildFilters = useCallback(() => {
    const filter = graphQLFilterFormatter(filterData, jobApplicantsColMapping);
    if (dateFrom) filter.push(`{ key: "createdAtFrom", value: "${dateFrom}" }`);
    if (dateTo) filter.push(`{ key: "createdAtTo", value: "${dateTo}" }`);
    selectedCompanies.forEach((c) =>
      filter.push(`{ key: "job.companyName", value: ${JSON.stringify(c)} }`),
    );
    selectedJobs.forEach((j) => filter.push(`{ key: "jobId", value: "${j.value}" }`));
    return filter;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData, dateFrom, dateTo, selectedCompanies, selectedJobs]);

  /** One-line description of the active filters, written into the sheet so the file explains itself. */
  const filterSummary = (): string => {
    const parts: string[] = [];
    filterData.filter(f => f.searchValue).forEach(f => parts.push(`${f.columnName}: ${f.searchValue}`));
    if (selectedCompanies.length) parts.push(`Company: ${selectedCompanies.join(', ')}`);
    if (selectedJobs.length) parts.push(`Job: ${selectedJobs.map(j => j.label).join(', ')}`);
    if (dateFrom || dateTo) parts.push(`Applied ${dateFrom || '…'} to ${dateTo || '…'}`);
    const when = formatDateTime(new Date());
    return parts.length ? `Filters: ${parts.join(' · ')} · exported ${when}` : `All applications · exported ${when}`;
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      // Export every row matching the filters, not the visible page. The old CSV exported `data`,
      // which is one page (100 rows) — so an export of a 500-row result silently produced 100 and
      // looked complete.
      const size = Math.max(totalElements, rowsPerPage);
      const { data: all } = await fetchGraphQLApiData(jobApplicantsList(0, size, buildFilters()));
      const content = all?.getAllJobApplications?.content ?? [];
      const rows = content.filter(Boolean).map((item: any) => ({
        name: candidateDisplayName(item),
        designation: item.job?.title || '',
        companyName: item.job?.companyName || '',
        status: item.status || '',
        stage: statusToStage(item.status) || '',
        detail: detailOf(item),
        hrIds: item.ownerId ? [item.ownerId] : item.assignedHRs ?? [],
        nextAction: item.nextAction || '',
        nextActionAt: item.nextActionAt || '',
        createdAt: item.createdAt || '',
        match: item.relevancyPercent ?? null,
      }));
      if (rows.length === 0) {
        showToast('Nothing to export for the current filters', 'info');
        return;
      }
      await exportApplicationsExcel(rows, hrById, filterSummary());
      showToast(`Exported ${rows.length} application${rows.length === 1 ? '' : 's'}`, 'success');
    } catch (e) {
      console.error('Export failed:', e);
      showToast('Failed to export applications', 'error');
    } finally {
      setExporting(false);
    }
  };

  // Opens the application workspace at its own route, rather than replacing
  // the list with a popup rendered in place.
  const handleOpenRecord = (id: string) => navigate(`/job-applications/${id}`);

  return (
    <CRMPageTemplate
      columnOptions={{ Stage: STATUS_VALUES, Detail: DETAIL_VALUES, 'Closed reason': CLOSED_REASONS }}
      filterColumns={filterColumns}
      filterLabel="Filter"
      icon={<AssignmentIcon />}
      initialColumns={filterData}
      setFilterData={setFilterData}
      title="Job Applications"
      totalCount={totalElements}
      onApplyFilters={onApplyFilters}
      dateFrom={dateFrom}
      dateTo={dateTo}
      onDateFromChange={v => setDateFrom(v)}
      onDateToChange={v => setDateTo(v)}
      topFilterContent={
        <>
          {/* Same .sf-drawer-field wrapper as the columns below; each uses the shared
              AutocompleteFormField (full width) so styling stays consistent. */}
          <Box className="sf-drawer-field">
            <Typography className="sf-drawer-field-label" variant="overline">
              Company Name
            </Typography>
            <AutocompleteFormField
              required={false}
              multiple
              fullWidth
              name="companyFilter"
              label=""
              size="small"
              placeholder="Search companies…"
              options={companyOpts}
              value={pendingCompanies}
              textFieldProps={{ fullWidth: true }}
              autocompleteProps={{ renderValue: () => null }}
              onInputChange={(_e: unknown, val: string) => onCompanyInput(val)}
              onChange={(_e: unknown, nv: string[]) => setPendingCompanies(nv)}
            />
            {pendingCompanies.length > 0 && (
              <Box className="sf-chips">
                {pendingCompanies.map((c) => (
                  <Chip
                    key={c}
                    className="sf-chip"
                    size="small"
                    label={c}
                    onDelete={() => setPendingCompanies(pendingCompanies.filter((x) => x !== c))}
                  />
                ))}
              </Box>
            )}
          </Box>
          <Box className="sf-drawer-field">
            <Typography className="sf-drawer-field-label" variant="overline">
              Job
            </Typography>
            <AutocompleteFormField
              required={false}
              multiple
              fullWidth
              name="jobFilter"
              label=""
              size="small"
              placeholder="Search jobs…"
              options={jobOptions}
              value={pendingJobs}
              getOptionLabel={(o: JobOption) => o?.label ?? ''}
              isOptionEqualToValue={(o: JobOption, v: JobOption) => o?.value === v?.value}
              textFieldProps={{ fullWidth: true }}
              autocompleteProps={{ renderValue: () => null }}
              onChange={(_e: unknown, nv: JobOption[]) => setPendingJobs(nv)}
            />
            {pendingJobs.length > 0 && (
              <Box className="sf-chips">
                {pendingJobs.map((j) => (
                  <Chip
                    key={j.value}
                    className="sf-chip"
                    size="small"
                    label={j.label}
                    onDelete={() => setPendingJobs(pendingJobs.filter((x) => x.value !== j.value))}
                  />
                ))}
              </Box>
            )}
          </Box>
        </>
      }
    >
      {loading ? (
        <SkeletonLoader rows={20} columns={APPLICANT_COLUMNS.length} />
      ) : error ? (
        <ErrorMessage message={error} />
      ) : data.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            py: 8,
          }}
        >
          <Typography color="text.secondary" variant="body2">
            No job applications found.
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
            {superAdmin && (
              <Button
                size="small"
                variant="contained"
                disabled={exportingWorkbook}
                startIcon={
                  exportingWorkbook ? <CircularProgress size={14} /> : <FileDownloadOutlinedIcon />
                }
                onClick={handleExportWorkbook}
              >
                {exportingWorkbook ? 'Preparing...' : 'Download Full Workbook'}
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              disabled={exporting}
              startIcon={exporting ? <CircularProgress size={14} /> : <FileDownloadOutlinedIcon />}
              onClick={handleExport}
            >
              {exporting ? 'Exporting...' : 'Export Excel'}
            </Button>
          </Box>
          <MintlyTable
            pagination={{
              enabled: true,
              page,
              rowsPerPage,
              totalNumberOfRows: totalElements,
              handleChangePage,
            }}
            rows={data}
            selectors={APPLICANT_COLUMNS}
            columns={APPLICANT_COLUMN_LAYOUT}
            getRowKey={(row: JobApplicant, i: number) => row.id ?? i}
            tableBodyRow={{
              name: (row: JobApplicant) => <>{row?.name || '-'}</>,
              match: (row: JobApplicant) => <MatchPercent value={row?.match} status={row?.matchStatus} />,
              designation: (row: JobApplicant) => <>{row?.designation || '-'}</>,
              companyName: (row: JobApplicant) => <>{row?.companyName || '-'}</>,
              status: (row: JobApplicant) => <CRMStatusChip status={row?.status} kind="application" />,
              detail: (row: JobApplicant) => <>{row?.detail || '-'}</>,
              // The candidate's own recruiter when there is one; the older assignment otherwise.
              hr: (row: JobApplicant) => (
                <>{row?.ownerId ? hrNamesFor([row.ownerId])[0] : row?.hrIds?.length ? hrNamesFor(row.hrIds).join(', ') : '-'}</>
              ),
              nextAction: (row: JobApplicant) => (
                <>
                  {row?.nextAction
                    ? `${row.nextAction}${row.nextActionAt ? ` (${formatDayMonth(row.nextActionAt)})` : ''}`
                    : '-'}
                </>
              ),
              createdAt: (row: JobApplicant) => (
                <>{formatDateTime(row?.createdAt)}</>
              ),
            }}
            tableHeadRow={{
              name: <>Candidate</>,
              match: <>AI match</>,
              designation: <>Position</>,
              companyName: <>Client</>,
              status: <>Stage</>,
              detail: <>Detail</>,
              hr: <>Recruiter</>,
              nextAction: <>Next action</>,
              createdAt: <>Applied On</>,
            }}
            onRowClick={(row) => handleOpenRecord(row.id)}
          />
        </>
      )}
    </CRMPageTemplate>
  );
};

export default JobApplicants;
